import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Loader2, RefreshCw, Unlink, ShieldCheck, BadgeCheck, AlertTriangle, ListOrdered } from "lucide-react";

const pick = (o, keys) => { for (const k of keys) { if (o && o[k] != null && o[k] !== "") return o[k]; } return null; };
const EVENT_KEYS = ["eventName", "event", "EventName", "Event", "eventAbbrev", "discipline", "Discipline"];
const PERF_KEYS = ["performance", "perf", "section", "round", "go", "Performance", "performanceName", "daySheet", "dayName", "sectionName"];
const POS_KEYS = ["drawNumber", "position", "order", "slot", "draw", "DrawNumber", "Position", "contestantNumber", "outNumber", "stockDraw"];
const STOCK_KEYS = ["stock", "animal", "horse", "bull", "stockName", "Stock", "drawStock"];
const DATE_KEYS = ["performanceDate", "perfDate", "date", "Date", "time", "Time"];

function getDrawLines(r) {
  const lines = [];
  for (const k of Object.keys(r || {})) {
    const v = r[k];
    if (Array.isArray(v) && v.length && typeof v[0] === "object") {
      for (const it of v) {
        const ln = { ev: pick(it, EVENT_KEYS), perf: pick(it, PERF_KEYS), pos: pick(it, POS_KEYS), stock: pick(it, STOCK_KEYS), dt: pick(it, DATE_KEYS) };
        if (ln.ev || ln.perf || ln.pos || ln.stock) lines.push(ln);
      }
    }
  }
  if (!lines.length) {
    const flat = { ev: pick(r, EVENT_KEYS), perf: pick(r, PERF_KEYS), pos: pick(r, POS_KEYS), stock: pick(r, STOCK_KEYS), dt: pick(r, DATE_KEYS) };
    if (flat.perf || flat.pos || flat.stock) lines.push(flat);
  }
  return lines;
}

function RodeoRow({ m, r }) {
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const name = r.rodeoName || r.RodeoName || r.name || r.Name || "Rodeo";
  const city = r.city || r.City || r.rodeoCity || "";
  const events = r.events || r.Events || r.eventList;
  const evText = Array.isArray(events) ? events.map((e) => (typeof e === "string" ? e : (e.eventName || e.name || e.EventName))).filter(Boolean).join(", ") : (typeof events === "string" ? events : "");
  const draws = getDrawLines(r);
  return (
    <div className={`rounded-lg px-3 py-2.5 text-sm ${m ? "bg-white/5" : "bg-[#FAFBFC] border border-[#EEF0F2]"}`} data-testid="prca-rodeo-row">
      <div className={`font-semibold ${title}`}>{name}</div>
      <div className="text-xs text-slate-400">{[city, evText].filter(Boolean).join(" · ")}</div>
      {draws.length > 0 && (
        <div className="mt-1.5 space-y-1" data-testid="prca-draw-lines">
          {draws.map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <ListOrdered className="w-3.5 h-3.5 text-[#003087] shrink-0" />
              <span className={title}>{[d.ev, d.perf].filter(Boolean).join(" · ")}</span>
              {d.pos != null ? <span className="font-bold text-[#003087]">draw #{d.pos}</span> : null}
              {d.stock ? <span className="text-slate-400">on {d.stock}</span> : null}
              {d.dt ? <span className="text-slate-400">{String(d.dt).slice(0, 10)}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PrcaConnect({ m }) {
  const [state, setState] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get("/me/prca").then((r) => setState(r.data)).catch(() => setState({ connected: false })), []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!username.trim() || !password) { toast.error("Enter your PRCA username and password"); return; }
    setConnecting(true);
    try {
      await api.post("/me/prca/connect", { username: username.trim(), password });
      setPassword("");
      toast.success("PRCA account connected");
      await load();
      sync();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConnecting(false); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/me/prca/sync"); setState(data); toast.success("Synced from PRCA"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };
  const disconnect = async () => {
    try { await api.delete("/me/prca"); toast.success("PRCA account disconnected"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const inp = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400";

  if (state === null) return <div className={`rounded-xl border p-6 flex justify-center ${card}`}><Loader2 className="w-5 h-5 animate-spin text-[#003087]" /></div>;

  if (!state.connected) {
    return (
      <div className={`rounded-xl border p-5 ${card}`} data-testid="prca-connect-card">
        <div className={`flex items-center gap-2 font-display font-bold mb-1 ${title}`}><Link2 className="w-4 h-4 text-[#003087]" /> Connect your PRCA account</div>
        <p className="text-sm text-slate-400 mb-3">Log in with your rodeoentry.prorodeo.org account to see your own PRCA entries and draws. Stored encrypted, only used when you tap Sync.</p>
        <div className="space-y-2">
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="PRCA username" data-testid="prca-username-input"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#003087] ${inp}`} />
          <input value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && connect()} type="password" placeholder="PRCA password" data-testid="prca-password-input"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#003087] ${inp}`} />
          <button onClick={connect} disabled={connecting} data-testid="prca-connect-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#003087] hover:bg-[#00246a] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Connect
          </button>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-3"><ShieldCheck className="w-3.5 h-3.5 text-[#3B6B1E]" /> Encrypted at rest · disconnect anytime</div>
      </div>
    );
  }

  const p = state.person || {};
  return (
    <div className="space-y-3">
      <div className={`rounded-xl border p-5 ${card}`} data-testid="prca-connected-card">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className={`flex items-center gap-2 font-display font-bold ${title}`}><ShieldCheck className="w-4 h-4 text-[#3B6B1E]" /> PRCA Connected</div>
          <button onClick={disconnect} data-testid="prca-disconnect-btn" className="text-xs font-semibold text-slate-400 hover:text-[#003087] flex items-center gap-1"><Unlink className="w-3.5 h-3.5" /> Disconnect</button>
        </div>
        <p className="text-sm text-slate-400 mb-1">
          {p.name ? <b className={title}>{p.name}</b> : <>@{state.username}</>}{p.card ? ` · card #${p.card}` : ""}{p.circuit ? ` · ${p.circuit}` : ""}
        </p>
        {p.eligible === false ? (
          <div className="flex items-center gap-1.5 text-xs text-[#B8860B] mb-3"><AlertTriangle className="w-3.5 h-3.5" /> Membership currently ineligible on PRCA</div>
        ) : p.eligible === true ? (
          <div className="flex items-center gap-1.5 text-xs text-[#3B6B1E] mb-3"><BadgeCheck className="w-3.5 h-3.5" /> Eligible member</div>
        ) : null}
        <button onClick={sync} disabled={syncing} data-testid="prca-sync-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#003087] hover:bg-[#00246a] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">
          {syncing ? <><Loader2 className="w-4 h-4 animate-spin" /> Syncing…</> : <><RefreshCw className="w-4 h-4" /> Sync my PRCA entries</>}
        </button>
      </div>

      {state.open_rodeos?.length > 0 && (
        <div className={`rounded-xl border p-5 ${card}`} data-testid="prca-open-rodeos">
          <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>Open entries ({state.open_rodeos.length})</div>
          <div className="space-y-1.5">{state.open_rodeos.map((r, i) => <RodeoRow key={i} m={m} r={r} />)}</div>
        </div>
      )}
      {state.closed_rodeos?.length > 0 && (
        <div className={`rounded-xl border p-5 ${card}`} data-testid="prca-closed-rodeos">
          <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>Closed / past entries ({state.closed_rodeos.length})</div>
          <div className="text-[11px] text-slate-400 mb-2">Your draw positions show here once the draw is posted.</div>
          <div className="space-y-1.5">{state.closed_rodeos.map((r, i) => <RodeoRow key={i} m={m} r={r} />)}</div>
        </div>
      )}
      {state.synced_at && !state.open_rodeos?.length && !state.closed_rodeos?.length && (
        <div className={`rounded-xl border border-dashed p-5 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`} data-testid="prca-no-entries">
          No current PRCA entries on your account.
        </div>
      )}
    </div>
  );
}
